import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  TrendingUp as TrendingUpIcon,
  Shield as ShieldIcon,
  Verified as VerifiedIcon,
  Accessibility as AccessibilityIcon,
  Architecture as ArchitectureIcon,
  LockOpen as LockOpenIcon,
  CloudDone as CloudDoneIcon
} from '@mui/icons-material';

const HighPerformance: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = React.useState(0);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const urls = [
      '/capabilities/high-performance',
      '/capabilities/zero-trust-security',
      '/capabilities/fedramp-ready',
      '/capabilities/section-508-compliant',
      '/capabilities/scalable-architecture',
      '/capabilities/no-vendor-lock-in'
    ];
    navigate(urls[newValue]);
  };

  const benefits = [
    '10x faster data discovery compared to traditional metadata tools',
    'Sub-second search response times across millions of assets',
    'Optimized caching reduces database load by 80%',
    'Horizontal scaling supports unlimited concurrent users',
    'Real-time indexing ensures data is always current',
    'Efficient query optimization minimizes resource consumption'
  ];

  const capabilities = [
    {
      icon: <MemoryIcon sx={{ color: 'primary.main' }} />,
      title: 'Advanced Caching',
      description: 'Multi-tier caching strategy with Redis and in-memory stores for instant data retrieval'
    },
    {
      icon: <StorageIcon sx={{ color: 'primary.main' }} />,
      title: 'Optimized Indexing',
      description: 'Elasticsearch-powered full-text search with custom analyzers for metadata'
    },
    {
      icon: <CloudIcon sx={{ color: 'primary.main' }} />,
      title: 'CDN Integration',
      description: 'Static assets delivered via CloudFront for global low-latency access'
    },
    {
      icon: <TrendingUpIcon sx={{ color: 'primary.main' }} />,
      title: 'Scalable Architecture',
      description: 'Horizontally scalable microservices adapt to changing workloads'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: 'primary.main' }}
      >
        Back to About Page
      </Button>

      {/* Capability Navigation Tabs */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="Core Capability tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ 
          mb: 4,
          '.MuiTab-root': {
            color: 'primary.main',
            minHeight: 64,
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 700
            }
          },
          '.MuiTabs-indicator': {
            backgroundColor: 'primary.main',
            height: 3
          }
        }}
      >
        <Tab 
          label="High Performance" 
          icon={<SpeedIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="Zero Trust" 
          icon={<ShieldIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="FedRAMP Ready" 
          icon={<CloudDoneIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="508 Compliant" 
          icon={<AccessibilityIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="Scalable" 
          icon={<TrendingUpIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="No Lock-In" 
          icon={<LockOpenIcon />} 
          iconPosition="start"
        />
      </Tabs>

      {/* Page Title */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SpeedIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
          <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            High Performance
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Sub-second search response times across millions of metadata assets
        </Typography>
      </Box>

      {/* Overview and Key Benefits - Side by Side */}
      <Paper sx={{ p: 4, mb: 4, borderLeft: '4px solid #003366' }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Overview
            </Typography>
            <Typography sx={{ fontSize: '0.95rem' }}>
              E-Unify's high-performance architecture is engineered to deliver exceptional speed and responsiveness across all operations. Through advanced caching strategies, optimized database queries, and intelligent resource management, the platform ensures that users can discover and access metadata instantly, regardless of scale.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Key Benefits
            </Typography>
            <List dense>
              {benefits.map((benefit, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={benefit}
                    primaryTypographyProps={{ fontSize: '0.95rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* Technical Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
        Technical Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #003366' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default HighPerformance;
